/**
 * Blog article table of contents.
 *
 * sections/main-article.liquid renders the empty <nav data-article-toc> (only
 * when the post has enough H2s) and the layout around it; this script gives the
 * body's H2s ids, builds the links, and highlights the section being read.
 * Mobile/tablet: collapsible box above the body. Desktop (>= 1200px): sticky
 * sidebar, always expanded.
 */
(() => {
  const desktop = window.matchMedia('(min-width: 1200px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const stickyHeaderHeight = () =>
    parseInt(getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height'), 10) || 0;

  const slugify = (text) =>
    text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'section';

  // Article images above the target can finish loading mid-scroll and push the
  // heading down, so re-align once scrolling settles.
  const scrollToHeading = (heading, smooth) => {
    heading.scrollIntoView({ behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto', block: 'start' });
    const realign = () => {
      const offset = parseFloat(getComputedStyle(heading).scrollMarginTop) || 0;
      if (Math.abs(heading.getBoundingClientRect().top - offset) > 4) heading.scrollIntoView({ block: 'start' });
    };
    const started = Date.now();
    const settle = () => {
      // No scroll was needed, so this is the end of some later manual scroll.
      if (Date.now() - started > 3000) return;
      realign();
      setTimeout(realign, 600);
    };
    if ('onscrollend' in window) window.addEventListener('scrollend', settle, { once: true });
    else setTimeout(settle, 900);
  };

  const uniqueId = (base) => {
    let id = base;
    let n = 2;
    while (document.getElementById(id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    return id;
  };

  function init(toc) {
    if (toc.dataset.articleTocReady) return;
    toc.dataset.articleTocReady = 'true';

    const section = toc.closest('.shopify-section') || document;
    const body = section.querySelector('[data-article-toc-body]');
    const list = toc.querySelector('[data-article-toc-list]');
    const toggle = toc.querySelector('.article-toc__toggle');
    const minHeadings = parseInt(toc.dataset.minHeadings, 10) || 2;
    const headings = body ? [...body.querySelectorAll('h2')].filter((h) => h.textContent.trim()) : [];

    // Liquid counted raw <h2 tags; if some are empty, fall back to the no-TOC layout.
    if (headings.length < minHeadings) {
      section.querySelectorAll('.article-frame--toc').forEach((el) => el.classList.remove('article-frame--toc'));
      toc.remove();
      return;
    }

    const links = headings.map((heading) => {
      if (!heading.id) heading.id = uniqueId(slugify(heading.textContent.trim()));

      const link = document.createElement('a');
      link.className = 'article-toc__link';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim().replace(/\s+/g, ' ');

      const item = document.createElement('li');
      item.appendChild(link);
      list.appendChild(item);
      return link;
    });

    // Collapsed on mobile, always expanded on desktop.
    const setOpen = (open) => {
      toc.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    const syncToViewport = () => setOpen(desktop.matches);
    syncToViewport();
    desktop.addEventListener('change', syncToViewport);
    toggle.addEventListener('click', () => {
      if (!desktop.matches) setOpen(!toc.classList.contains('is-open'));
    });

    list.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link) return;
      const heading = document.getElementById(link.hash.slice(1));
      if (!heading) return;

      event.preventDefault();
      // Close the mobile box first: it sits above the body, so collapsing it moves the target.
      if (!desktop.matches) setOpen(false);
      scrollToHeading(heading, true);
      history.replaceState(null, '', link.hash);
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    });

    // Highlight the last heading that has scrolled past the sticky header.
    const activeLine = () => stickyHeaderHeight() + 40;
    const setActive = () => {
      const threshold = activeLine();
      let active = -1;
      headings.forEach((heading, i) => {
        if (heading.getBoundingClientRect().top <= threshold) active = i;
      });
      links.forEach((link, i) => {
        if (i === active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };
    // Scroll listener rather than IntersectionObserver: a big jump (End key, fast
    // fling) can carry a heading past the line without an intersection change.
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          setActive();
          ticking = false;
        });
      },
      { passive: true }
    );
    setActive();

    // A shared #link to a heading only resolves now that the ids exist.
    if (window.location.hash) {
      const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      if (target && headings.includes(target)) scrollToHeading(target, false);
    }
  }

  const initAll = (root = document) => root.querySelectorAll('[data-article-toc]').forEach(init);

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
